import { useEffect, useState } from 'react';
import { ImageField } from '@/components/ui/ImageField';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';

import { coursesApi, type AccessMode, type CEFRLevel, type Language, type Marathon, type RatingScope } from '@/api/courses';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { useIsMobile } from '@/hooks/useIsMobile';
import { humanizeError } from '@/lib/errors';
import { marathonFormValues, type MarathonFormData } from '@/lib/marathonForm';

// Школа английского: курс = один из трёх направлений (TZ — IELTS / Pre-Upper / Beginner).
const CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'pre-upper', label: 'Pre-Upper' },
  { value: 'ielts', label: 'IELTS' },
];

const CEFR_OPTIONS: { value: CEFRLevel; label: string }[] = [
  { value: 'A1', label: 'A1' },
  { value: 'A2', label: 'A2' },
  { value: 'B1', label: 'B1' },
  { value: 'B2', label: 'B2' },
  { value: 'C1', label: 'C1' },
  { value: 'C2', label: 'C2' },
];

const LANG_OPTIONS: { value: Language; label: string }[] = [
  { value: 'EN', label: 'English' },
  { value: 'RU', label: 'Русский' },
  { value: 'KZ', label: 'Қазақша' },
];

export type MarathonFormModalProps = {
  open: boolean;
  onClose: () => void;
  initial?: Marathon;
  onCreated?: (m: Marathon) => void;
};

/**
 * Create/edit form for a marathon. Used both from the marathons list
 * (create new) and from the marathon detail page (edit existing).
 */
export function MarathonFormModal({ open, onClose, initial, onCreated }: MarathonFormModalProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [serverError, setServerError] = useState<string | null>(null);
  const isEdit = !!initial;

  const RATING_SCOPE_OPTIONS: { value: RatingScope; label: string }[] = [
    { value: 'all', label: t('marathonForm.ratingAll') },
    { value: 'top_5', label: t('marathonForm.ratingTop5') },
    { value: 'top_10', label: t('marathonForm.ratingTop10') },
  ];

  /**
   * ⚠️ РЕЖИМА «ЖДАТЬ ПРОВЕРКИ КУРАТОРА» ЗДЕСЬ БОЛЬШЕ НЕТ, И ЭТО РЕШЕНИЕ
   * ЗАКАЗЧИКА (06.09.2026).
   *
   * Он работал так: следующий раздел открывался, только когда куратор ПРИНЯЛ
   * домашку предыдущего. То есть ученик всё сделал, а идти дальше не может —
   * ждёт человека. На потоке в 814 учеников это значит очередь: пока куратор
   * не дошёл до работы, ученик стоит.
   *
   * Заказчик: «главное — они выполнили всё в разделе, факт остаётся фактом.
   * А доработка, если куратор отклонит, идёт своим чередом в разделе домашки».
   *
   * Убран сам выбор, а не только подпись: пока он в списке, его однажды нажмут.
   * Сервер тоже считает такой режим последовательным — см. applyAccessMode.
   */
  const ACCESS_OPTIONS: { value: AccessMode; label: string }[] = [
    { value: 'open', label: t('marathonForm.accessOpen') },
    { value: 'sequential', label: t('marathonForm.accessSequential') },
  ];

  const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm<MarathonFormData>({
    defaultValues: marathonFormValues(initial),
  });

  // При каждом открытии — значения текущего курса (разбор удобства 28.09, №7): окно не пересоздаётся, и без этого
  // второе открытие показывало бы форму в том виде, в каком её оставили, а «Сохранить» слало бы старое.
  useEffect(() => {
    if (open) reset(marathonFormValues(initial));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- только на открытие: правку, идущую в окне, не трогаем
  }, [open]);

  const save = useMutation({
    mutationFn: async (data: MarathonFormData) => {
      // ⚠️ Пустое поле даты должно уехать как NULL, а не пустой строкой:
      // пустая строка не разбирается в дату, и сервер отдал бы ошибку вместо
      // понятного «обучение уже идёт».
      // ⚠️ Отправляем голую дату — час открытия (6 утра по Казахстану)
      // проставляет сервер. Одно правило в одном месте: будь оно и здесь, и
      // там, однажды они разъехались бы, и никто бы не заметил.
      // Пустое число порядка (valueAsNumber даёт NaN) — ноль, а не NaN в теле запроса.
      const payload = {
        ...data,
        display_order: Number.isFinite(data.display_order) ? Math.trunc(data.display_order) : 0,
        starts_at: data.starts_at || null,
      };
      if (isEdit && initial) {
        return coursesApi.updateMarathon(initial.id, payload);
      }
      return coursesApi.createMarathon(payload);
    },
    onSuccess: (m) => {
      onCreated?.(m);
      // Правка — встаём на то, что вернул сервер; создание — чистая форма для следующего курса.
      // `reset()` без значений возвращал форму к значениям ДО правки, и повторное сохранение её откатывало.
      reset(marathonFormValues(isEdit ? m : undefined));
      onClose();
    },
    onError: (e: unknown) => setServerError(humanizeError(e)),
  });

  // На телефоне — внизу окна во весь экран (§5.3, правило 3): 15 полей, и «Сохранить» в конце формы уезжало на три
  // экрана вниз. Полоса вне <form> — отправка по `form`.
  const buttons = (
    <div className={isMobile ? 'grid grid-cols-2 gap-2' : 'flex justify-end gap-2 pt-2'}>
      <Button type="button" variant="ghost" onClick={onClose}>
        {t('common.cancel')}
      </Button>
      <Button type="submit" form="marathon-form" loading={save.isPending}>
        {isEdit ? t('common.save') : t('marathonForm.create')}
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? t('marathonForm.editTitle') : t('marathonForm.createTitle')}
      size="lg"
      phoneFull
      footer={isMobile ? buttons : undefined}
    >
      <form id="marathon-form" onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-4">
        <Input
          label={t('marathonForm.labelTitle')}
          placeholder="IELTS Academic Sprint"
          {...register('title', { required: true })}
          error={errors.title && t('marathonForm.errorTitleRequired')}
        />
        <Input
          label={t('marathonForm.labelSlug')}
          placeholder="ielts-academic-sprint"
          hint={t('marathonForm.hintSlug')}
          {...register('slug', { required: true, pattern: /^[a-z0-9-]+$/ })}
          error={errors.slug && (errors.slug.type === 'required' ? t('marathonForm.errorSlugRequired') : t('marathonForm.errorSlugPattern'))}
        />

        {/*
          Обложка и фон загружаются К НАМ. Раньше это были поля со ссылкой, и в
          боевых курсах там стоял адрес чужого сервиса: удалят файл — обложка
          пропадёт у всех сразу. Поле адреса осталось видимым, потому что у
          существующих курсов там уже есть значения.
        */}
        <div className="grid gap-4 md:grid-cols-2">
          <ImageField
            label={t('marathonForm.labelCoverUrl')}
            value={watch('cover_url') ?? ''}
            onChange={(url) => setValue('cover_url', url, { shouldDirty: true })}
            category="lesson_covers"
            hint={t('marathonForm.hintCover')}
          />
          <ImageField
            label={t('marathonForm.labelBackgroundUrl')}
            value={watch('background_url') ?? ''}
            onChange={(url) => setValue('background_url', url, { shouldDirty: true })}
            category="lesson_covers"
          />
        </div>

        <Select label={t('marathonForm.labelCategory')} options={CATEGORY_OPTIONS} {...register('category')} />

        <div className="grid gap-4 md:grid-cols-3">
          <Select label={t('marathonForm.labelLanguage')} options={LANG_OPTIONS} {...register('language')} />
          <Select label={t('marathonForm.labelLevelStart')} options={CEFR_OPTIONS} {...register('level_start')} />
          <Select label={t('marathonForm.labelLevelEnd')} options={CEFR_OPTIONS} {...register('level_end')} />
        </div>

        <Select label={t('marathonForm.labelAccessMode')} options={ACCESS_OPTIONS} {...register('access_mode')} />

        {/* ⚠️ Тариф и старт — разные вещи. Тарифы раздают заранее; до этой даты
            ученик с тарифом не получает ни одной платной возможности и видит
            только бесплатные курсы. Пусто — обучение уже идёт. */}
        <label className="block text-sm text-on-surface">
          {t('marathonForm.labelStartsAt')}
          <input
            type="date"
            {...register('starts_at')}
            className="app-field mt-1 w-full rounded-button px-3 py-2 text-sm"
          />
          <span className="mt-1 block text-xs text-on-surface-variant">
            {t('marathonForm.startsAtHint')}
          </span>
        </label>

        <label className="flex items-start gap-3 text-sm text-on-surface">
          <input type="checkbox" {...register('is_private')} className="mt-0.5 h-4 w-4 accent-primary" />
          <span>
            {t('marathonForm.isPrivate')}
            <span className="mt-0.5 block text-xs text-on-surface-variant">{t('marathonForm.isPrivateHint')}</span>
          </span>
        </label>

        {/* Техдолг 321 (27.09.2026): до этого курс открывали для всех разовым SQL, а порядок каталога не задавался вовсе. */}
        <label className="flex items-start gap-3 text-sm text-on-surface">
          <input type="checkbox" {...register('is_free')} className="mt-0.5 h-4 w-4 accent-primary" />
          <span>
            {t('marathonForm.isOpen')}
            <span className="mt-0.5 block text-xs text-on-surface-variant">{t('marathonForm.isOpenHint')}</span>
          </span>
        </label>

        <Input
          type="number"
          step={1}
          label={t('marathonForm.labelDisplayOrder')}
          hint={t('marathonForm.hintDisplayOrder')}
          {...register('display_order', { valueAsNumber: true })}
        />

        <label className="flex items-center gap-3 text-sm text-on-surface">
          <input type="checkbox" {...register('show_rating')} className="h-4 w-4 accent-primary" />
          {t('marathonForm.showRating')}
        </label>

        <Select label={t('marathonForm.labelRatingScope')} options={RATING_SCOPE_OPTIONS} {...register('rating_scope')} />

        <label className="flex items-center gap-3 text-sm text-on-surface">
          <input type="checkbox" {...register('show_student_rank')} className="h-4 w-4 accent-primary" />
          {t('marathonForm.showStudentRank')}
        </label>

        {serverError && <p className="text-sm text-error">{serverError}</p>}

        {!isMobile && buttons}
      </form>
    </Modal>
  );
}
